import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { ApiError, api, isSuccessResponse } from "lib/api";
import { API_ENDPOINTS } from "lib/apiUrl";
import swtAlert from "lib/sweetAlert";
import { PROJECT_DETAILS_TABS } from "pages/project/details/projectDetailsTabs";
import { normalizeProjectDetail } from "utils/projectsList";

const ProjectDetailsContext = createContext(null);

export { PROJECT_DETAILS_TABS };

export function ProjectDetailsProvider({ projectId, children }) {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [activeTabId, setActiveTabId] = useState(PROJECT_DETAILS_TABS[0].id);

  const loadProject = useCallback(async () => {
    if (!projectId) {
      setMissing(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setMissing(false);
    setLoadError("");
    try {
      const path = API_ENDPOINTS.adminProjectById(projectId);
      const resp = await api.get(path);
      if (!isSuccessResponse(resp)) {
        const msg = resp?.message || "Could not load project.";
        swtAlert.error("Project", msg);
        setLoadError(msg);
        setProject(null);
        return;
      }
      setProject(normalizeProjectDetail(resp, projectId));
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        setMissing(true);
      } else {
        const msg = error?.message || "Could not load project.";
        swtAlert.error("Project", msg);
        setLoadError(msg);
      }
      setProject(null);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  const tabContent = useMemo(() => {
    if (!project) return null;
    return {
      projectOverview:
        project.projectOverview ||
        project.description ||
        "Detailed project overview will be available once submitted by the issuer.",
      overviewImageUrl: project.overviewImageUrl || "",
      companyOverview:
        project.companyOverview ||
        "Company profile and legal details are being reviewed and will be shared here.",
      offeringDocument:
        project.offeringDocumentDetails ||
        "Offering documents are available on request and will be listed here after compliance verification.",
    };
  }, [project]);

  const activeTab = PROJECT_DETAILS_TABS.find((tab) => tab.id === activeTabId) || PROJECT_DETAILS_TABS[0];

  const value = useMemo(
    () => ({
      project,
      loading,
      loadError,
      missing,
      reload: loadProject,
      tabs: PROJECT_DETAILS_TABS,
      activeTab,
      activeTabId,
      setActiveTabId,
      tabContent,
    }),
    [project, loading, loadError, missing, loadProject, activeTab, activeTabId, tabContent],
  );

  return <ProjectDetailsContext.Provider value={value}>{children}</ProjectDetailsContext.Provider>;
}

ProjectDetailsProvider.propTypes = {
  projectId: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export function useProjectDetailsContext() {
  const context = useContext(ProjectDetailsContext);
  if (!context) {
    throw new Error("useProjectDetailsContext must be used inside ProjectDetailsProvider");
  }
  return context;
}
